"use client";

import { useMemo, useState, type ReactNode } from "react";
import { Select } from "@/components/ui/select";
import { Icon } from "@/components/ui/icon";
import type { InputVariantProps } from "@/components/ui/input";
import { useGetJob } from "@/lib/api/generated";
import type { PipelineStage } from "@/lib/api/types";

import { StageDot } from "./stage-dot";

interface StageSelectProps {
  jobId: string | null;
  value: string | null;
  onChange: (stageId: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  variant?: InputVariantProps["variant"];
  size?: InputVariantProps["size"];
  /** Highlight the first stage (position 0) with a "Recommended" badge. */
  showRecommendedBadge?: boolean;
}

function RecommendedBadge() {
  return (
    <span className="shrink-0 rounded-sm border border-ai/15 bg-ai/10 px-1.5 py-0.5 text-xs font-medium leading-4 text-ai">
      Recommended
    </span>
  );
}

export function StageSelect({
  jobId,
  value,
  onChange,
  disabled,
  placeholder = "Select Stage",
  className,
  variant,
  size,
  showRecommendedBadge = false,
}: StageSelectProps) {
  const [open, setOpen] = useState(false);

  const { data: job, isLoading } = useGetJob(jobId ?? "", {
    query: { enabled: !!jobId },
  });

  const sortedStages = useMemo<PipelineStage[]>(
    () => [...(job?.stages ?? [])].sort((a, b) => a.position - b.position),
    [job?.stages],
  );

  const isRecommended = (stage: PipelineStage) =>
    showRecommendedBadge && stage.position === 0;

  const selected = sortedStages.find((s) => s.id === value) ?? null;
  const isDisabled =
    disabled || !jobId || isLoading || sortedStages.length === 0;

  let trigger: ReactNode;
  if (selected) {
    trigger = (
      <>
        <StageDot color={selected.color} />
        <span className="min-w-0 flex-1 truncate text-left text-heading">
          {selected.name}
        </span>
        {isRecommended(selected) && <RecommendedBadge />}
      </>
    );
  } else {
    trigger = (
      <>
        <StageDot />
        <span className="min-w-0 flex-1 truncate text-left text-contrast/50">
          {placeholder}
        </span>
      </>
    );
  }

  return (
    <Select.Root open={open} onOpenChange={setOpen}>
      <Select.Trigger
        disabled={isDisabled}
        className={className}
        variant={variant}
        size={size}
      >
        {trigger}
        <Select.Icon className="ml-auto" />
      </Select.Trigger>
      <Select.Content>
        <Select.List>
          {sortedStages.map((stage) => {
            const active = stage.id === value;
            return (
              <Select.Item
                key={stage.id}
                selected={active}
                onClick={() => {
                  onChange(stage.id);
                  setOpen(false);
                }}
              >
                <StageDot color={stage.color} />
                <span className="text-sm text-heading truncate text-left flex-1">
                  {stage.name}
                </span>
                {isRecommended(stage) && <RecommendedBadge />}
                {active && (
                  <Icon
                    name="IconCheckmark1"
                    size={14}
                    className="text-heading opacity-50 shrink-0"
                  />
                )}
              </Select.Item>
            );
          })}
        </Select.List>
      </Select.Content>
    </Select.Root>
  );
}
